import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, Sparkles, Target, Trophy, ChevronRight } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { ProgressBar } from '../components/ui/Progress';
import { useAppStore } from '../app/hooks/useAppStore';
import { MODULES, type ModuleId, getFamiliesForModule, type FamilyDefinition, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../content';
import type { FamilyMasteryState } from '../mastery';
import { getFamilyMasteryKey, getMasteryColor, getMasteryBgColor, getMasteryLevel } from '../mastery';
import { DjayAvatar, useDjaySelection } from '../mascot';
// ─── Types ────────────────────────────────────────────────────
export type FamilyPickerMode = 'sprint' | 'challenge' | 'free';
interface FamilyPickerProps {
  onClose: () => void;
  onSelect: (familyId: string) => void;
  mode: FamilyPickerMode;
}
interface FamilyWithMastery {
  family: FamilyDefinition;
  mastery: FamilyMasteryState | null;
  masteryScore: number;
  attempts: number;
}
// ─── Mastery Ring Colors ──────────────────────────────────────
function getMasteryRingColor(score: number): string {
  if (score >= 80) return '#6366F1'; // indigo
  if (score >= 50) return '#F59E0B'; // amber
  if (score >= 25) return '#F97316'; // orange
  return '#94A3B8'; // slate
}
// ─── Mode Config ──────────────────────────────────────────────
const MODE_CONFIG = {
  sprint: {
    gradient: 'from-indigo-500 via-blue-500 to-cyan-400',
    accent: 'indigo',
    message: 'Kies een onderwerp om te trainen! 🎯',
    emotion: 'pointing' as const,
    title: 'Focus Sprint'
  },
  free: {
    gradient: 'from-emerald-500 via-teal-500 to-cyan-400',
    accent: 'teal',
    message: 'Kies wat je wilt oefenen — helemaal vrij! 🎲',
    emotion: 'happy' as const,
    title: 'Vrij Oefenen'
  },
  challenge: {
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-400',
    accent: 'violet',
    message: 'Kies je uitdaging! Dit wordt spannend 💪',
    emotion: 'excited' as const,
    title: 'Uitdaging'
  }
} as const;
// ─── Main Component ───────────────────────────────────────────
/**
 * FamilyPicker — Full-screen family selection for practice modes.
 *
 * Supports sprint (weakest first), challenge (strongest first), and free (all families).
 * Features module filtering, search, Djay's pick highlight, and mastery rings.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function FamilyPicker({
  onClose,
  onSelect,
  mode
}: FamilyPickerProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const familyMastery = state.familyMastery || {};
  const [selectedModuleId, setSelectedModuleId] = useState<ModuleId | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const config = MODE_CONFIG[mode];
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  // Get all families with mastery data, filtered and sorted by mode
  const familiesWithMastery = useMemo(() => {
    const result: FamilyWithMastery[] = [];
    const modulesToCheck = selectedModuleId === 'all' ? MODULES.map(m => m.id) : [selectedModuleId];
    for (const moduleId of modulesToCheck) {
      const families = getFamiliesForModule(moduleId);
      for (const family of families) {
        if (mode !== 'free' && !allowedTiers.has(family.tier)) continue;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey] || null;
        const masteryScore = mastery?.masteryScore ?? 0;
        const attempts = mastery?.attempts ?? 0;
        if (mode === 'challenge' && masteryScore < 70) continue;
        if (mode === 'sprint' && attempts === 0) continue;
        result.push({
          family,
          mastery,
          masteryScore,
          attempts
        });
      }
    }
    if (mode === 'sprint') result.sort((a, b) => a.masteryScore - b.masteryScore);else if (mode === 'challenge') result.sort((a, b) => b.masteryScore - a.masteryScore);else result.sort((a, b) => a.family.description.localeCompare(b.family.description));
    return result;
  }, [selectedModuleId, selectedGroup, familyMastery, allowedTiers, mode]);
  // Filter by search query
  const filteredFamilies = useMemo(() => {
    if (!searchQuery.trim()) return familiesWithMastery;
    const query = searchQuery.toLowerCase();
    return familiesWithMastery.filter(({
      family
    }) => family.familyId.toLowerCase().includes(query) || family.description.toLowerCase().includes(query) || family.skill.toLowerCase().includes(query) || family.subSkill.toLowerCase().includes(query));
  }, [familiesWithMastery, searchQuery]);
  const hasPracticedFamilies = useMemo(() => Object.values(familyMastery).some(m => m.attempts > 0), [familyMastery]);
  const hasChallengeReadyFamilies = useMemo(() => Object.values(familyMastery).some(m => m.masteryScore >= 70), [familyMastery]);
  const showDjayPick = !searchQuery && selectedModuleId === 'all' && mode !== 'free' && filteredFamilies.length > 0;
  return <div className="bg-slate-50 pb-4" data-id="element-1659">
      {/* ── Colorful Header ── */}
      <div className={`bg-gradient-to-br ${config.gradient} relative overflow-hidden -mx-6 md:-mx-10 -mt-6 md:-mt-10`} data-id="element-1660">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1661" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-1662" />

        <div className="relative z-10 px-6 sm:px-8 md:px-10 pt-6 md:pt-10 pb-6 max-w-4xl mx-auto" data-id="element-1663">
          <button onClick={onClose} className="flex items-center gap-2 text-white/80 hover:text-white font-bold mb-4 transition-colors text-sm" data-id="element-1664">
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-1665">
              <ArrowLeft className="w-4 h-4" data-id="element-1666" />
            </div>
            Terug naar Oefenlab
          </button>

          <div className="flex items-center gap-4" data-id="element-1667">
            <motion.div className="flex-shrink-0" animate={{
            y: [0, -4, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1668">
              <DjayAvatar size="lg" emotion={config.emotion} variant={selectedVariant} animate data-id="element-1669" />
            </motion.div>

            <div className="flex-1 min-w-0" data-id="element-1670">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight flex items-center gap-3" data-id="element-1671">
                {config.title}
                {mode === 'free' && <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg uppercase tracking-wider" data-id="element-1672">
                    Experimenteel
                  </span>}
              </h1>
              <div className="mt-2 bg-white/20 backdrop-blur-sm rounded-2xl px-3 py-2 inline-block max-w-full" data-id="element-1673">
                <p className="text-white text-sm font-bold truncate" data-id="element-1674">
                  {config.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky Filters ── */}
      <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200/60 -mx-6 md:-mx-10" data-id="element-1675">
        <div className="px-6 sm:px-8 md:px-10 max-w-4xl mx-auto py-3 space-y-3" data-id="element-1676">
          <div className="flex overflow-x-auto pb-1 -mx-6 px-6 sm:mx-0 sm:px-0 hide-scrollbar gap-2" data-id="element-1677">
            <button onClick={() => setSelectedModuleId('all')} className={`flex-shrink-0 px-4 py-2 rounded-xl font-bold text-xs transition-all border-2 ${selectedModuleId === 'all' ? 'bg-slate-800 text-white border-slate-800 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-1678">
              Alle vakken
            </button>
            {MODULES.map(module => {
            const ModIcon = getModuleIcon(module.icon);
            const colors = getModuleColors(module.color);
            const isActive = selectedModuleId === module.id;
            return <button key={module.id} onClick={() => setSelectedModuleId(module.id)} className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs transition-all border-2 ${isActive ? `${colors.light} ${colors.text} ${colors.border} shadow-md` : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-1679">
                  <ModIcon className={`w-3.5 h-3.5 ${isActive ? colors.text : 'text-slate-400'}`} data-id="element-1680" />
                  <span className="hidden sm:inline" data-id="element-1681">{module.title}</span>
                  <span className="sm:hidden" data-id="element-1682">
                    {module.title.split(' ')[0]}
                  </span>
                </button>;
          })}
          </div>

          <Input type="text" placeholder="Zoek een onderwerp..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-1684" />} variant="student" size="sm" data-id="element-1683" />
        </div>
      </div>

      {/* ── Family Grid ── */}
      <div className="max-w-4xl mx-auto pt-4" data-id="element-1685">
        <div className="flex items-center justify-between mb-3" data-id="element-1686">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1687">
            {filteredFamilies.length} onderwerp
            {filteredFamilies.length !== 1 ? 'en' : ''}
          </p>
        </div>

        {filteredFamilies.length === 0 ? <EmptyState mode={mode} hasPracticedFamilies={hasPracticedFamilies} hasChallengeReadyFamilies={hasChallengeReadyFamilies} selectedVariant={selectedVariant} data-id="element-1688" /> : <div className="space-y-2" data-id="element-1689">
            {showDjayPick && <DjayPickCard item={filteredFamilies[0]} onSelect={onSelect} accentColor={config.accent} data-id="element-1690" />}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-id="element-1691">
              {filteredFamilies.slice(showDjayPick ? 1 : 0).map(({
            family,
            masteryScore,
            attempts
          }, index) => <FamilyCard key={family.familyId} family={family} masteryScore={masteryScore} attempts={attempts} index={index} onSelect={onSelect} data-id="element-1692" />)}
            </div>
          </div>}
      </div>
    </div>;
}
// ─── Sub-components ───────────────────────────────────────────
function EmptyState({
  mode,
  hasPracticedFamilies,
  hasChallengeReadyFamilies,
  selectedVariant
}: {
  mode: FamilyPickerMode;
  hasPracticedFamilies: boolean;
  hasChallengeReadyFamilies: boolean;
  selectedVariant: string;
}) {
  const title = mode === 'sprint' && !hasPracticedFamilies ? 'Je moet eerst meer oefenen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Nog geen onderwerpen klaar' : 'Geen resultaten gevonden';
  const message = mode === 'sprint' && !hasPracticedFamilies ? 'Maak eerst wat oefeningen in een vak. Daarna kun je hier extra trainen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Oefen meer tot je minimaal 70% beheersing hebt.' : 'Probeer een andere zoekopdracht of selecteer een ander vak.';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16 text-center px-6" data-id="element-1693">
      <div className="mb-4" data-id="element-1694">
        <DjayAvatar size="xl" emotion="thinking" variant={selectedVariant} data-id="element-1695" />
      </div>
      <h3 className="text-xl font-black text-slate-800 mb-2" data-id="element-1696">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm font-medium leading-relaxed" data-id="element-1697">
        {message}
      </p>
    </motion.div>;
}
function FamilyCard({
  family,
  masteryScore,
  attempts,
  index,
  onSelect
}: {
  family: FamilyDefinition;
  masteryScore: number;
  attempts: number;
  index: number;
  onSelect: (id: string) => void;
}) {
  const module = MODULES.find(m => m.id === family.module);
  const ModIcon = getModuleIcon(module?.icon || '');
  const colors = module ? getModuleColors(module.color) : null;
  return <motion.button initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.03, 0.3)
  }} onClick={() => onSelect(family.familyId)} className="w-full text-left p-3 rounded-2xl border-2 border-slate-200 bg-white hover:border-indigo-300 hover:shadow-lg active:scale-[0.98] transition-all group relative overflow-hidden" data-id="element-1698">
      <div className="flex items-start gap-3" data-id="element-1699">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors?.light || 'bg-slate-100'}`} data-id="element-1700">
          <ModIcon className={`w-5 h-5 ${colors?.text || 'text-slate-500'}`} data-id="element-1701" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1702">
          <h4 className="text-sm font-black text-slate-800 leading-tight line-clamp-2 group-hover:text-indigo-700 transition-colors" data-id="element-1703">
            {family.description}
          </h4>
          <div className="flex items-center gap-1.5 mt-1" data-id="element-1704">
            <Badge variant="neutral" size="sm" data-id="element-1705">
              {family.levelCode}
            </Badge>
            <span className="text-[10px] text-slate-400 font-medium truncate" data-id="element-1706">
              {family.skill}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col items-center" data-id="element-1707">
          <MasteryRing score={masteryScore} size={36} data-id="element-1708" />
          {attempts > 0 && <span className="text-[9px] font-bold text-slate-400 mt-0.5" data-id="element-1709">
              {attempts}×
            </span>}
        </div>
      </div>
      <div className="mt-2.5" data-id="element-1710">
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-1711">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: getMasteryRingColor(masteryScore)
        }} initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, masteryScore))}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-1712" />
        </div>
      </div>
    </motion.button>;
}
function DjayPickCard({
  item,
  onSelect,
  accentColor
}: {
  item: FamilyWithMastery;
  onSelect: (familyId: string) => void;
  accentColor: string;
}) {
  const {
    family,
    masteryScore,
    attempts
  } = item;
  const module = MODULES.find(m => m.id === family.module);
  const ModIcon = getModuleIcon(module?.icon || '');
  const colors = module ? getModuleColors(module.color) : null;
  return <motion.button initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} onClick={() => onSelect(family.familyId)} className="w-full text-left mb-2 p-4 rounded-2xl border-3 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 hover:shadow-xl active:scale-[0.99] transition-all group relative overflow-hidden" data-id="element-1713">
      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-1714" />
      <div className="flex items-center gap-1.5 mb-3" data-id="element-1715">
        <div className="bg-amber-200/60 text-amber-600 p-1 rounded-lg" data-id="element-1716">
          <Sparkles className="w-3.5 h-3.5" data-id="element-1717" />
        </div>
        <span className="text-[11px] font-black text-amber-600 uppercase tracking-wider" data-id="element-1718">
          Djay's keuze — begin hier!
        </span>
      </div>

      <div className="flex items-center gap-3 relative z-10" data-id="element-1719">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm" data-id="element-1720">
          <ModIcon className={`w-6 h-6 ${colors?.text || 'text-slate-500'}`} data-id="element-1721" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1722">
          <h4 className="text-base font-black text-slate-900 leading-tight line-clamp-2" data-id="element-1723">
            {family.description}
          </h4>
          <div className="flex items-center gap-2 mt-1" data-id="element-1724">
            <Badge variant="warning" size="sm" data-id="element-1725">
              {family.levelCode}
            </Badge>
            <span className="text-xs text-slate-500 font-medium truncate" data-id="element-1726">
              {family.skill}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2" data-id="element-1727">
          <div className="flex flex-col items-center" data-id="element-1728">
            <MasteryRing score={masteryScore} size={44} data-id="element-1729" />
            <span className={`text-[9px] font-bold mt-0.5 px-1.5 py-0.5 rounded ${getMasteryBgColor(masteryScore)} ${getMasteryColor(masteryScore)}`} data-id="element-1730">
              {getMasteryLevel(masteryScore)}
            </span>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 transition-transform" data-id="element-1731" />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3" data-id="element-1732">
        <div className="flex-1 h-2 bg-white/80 rounded-full overflow-hidden" data-id="element-1733">
          <motion.div className="h-full rounded-full bg-amber-400" initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, masteryScore))}%`
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} data-id="element-1734" />
        </div>
        <span className="text-xs font-bold text-slate-500 flex-shrink-0" data-id="element-1735">
          {attempts}× geoefend
        </span>
      </div>
    </motion.button>;
}
/** Compact SVG mastery ring with animated progress arc */
function MasteryRing({
  score,
  size = 36
}: {
  score: number;
  size?: number;
}) {
  const strokeWidth = 3;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, Math.max(0, score));
  const offset = circumference - progress / 100 * circumference;
  const color = getMasteryRingColor(score);
  return <div className="relative" style={{
    width: size,
    height: size
  }} data-id="element-1736">
      <svg width={size} height={size} className="-rotate-90" data-id="element-1737">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} data-id="element-1738" />
        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset: offset
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-1739" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-1740">
        <span className="text-[10px] font-black text-slate-700" data-id="element-1741">
          {Math.round(score)}
        </span>
      </div>
    </div>;
}